import React, { Component } from 'react'
import { AiOutlineReload, AiOutlineTag, AiOutlineWarning, AiFillFileExcel } from "react-icons/ai"
import { getInformationWithDataV3 } from '../../services/CABE'
import { getValueCookie } from '../../services/cookieService'
import { NumberFormat } from '../../functions/generalFunctions'
import { pricesFormat } from '../../functions/pricesFormat'
import { DateFormatMAS90 } from '../../functions/dateFormat'
import { getDataSet } from '../../functions/generateDataSetExcel'
import '../../css/dashboard.css'
import { Menu } from "../../components/Menu/Menu"
import LoadingWindow from '../../components/LoadingWindow'
import ExcelDocument from '../../components/ExcelDocument'

const BTN_SECONDARY = { background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', color: '#0F172A', fontWeight: 600, padding: '0.4rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer' }
// More breathing room between rows (2026-08-26, client's ask — "unos 5px
// mas") — dashboard.css's own .table-clean tbody td default is 10px 16px;
// this overrides it per-cell since inline styles win over the class.
const CELL_PADDING = { padding: '15px 16px' }

function formatDateInput(date) {
    return date.toISOString().slice(0, 10)
}
function addDays(date, days) {
    const d = new Date(date)
    d.setDate(d.getDate() + days)
    return d
}
// DateFormatMAS90 expects a plain "YYYY-MM-DD" string (it does date.split('-')
// and assumes exactly 3 parts) — RDS returns full ISO datetimes
// ("2026-08-24T06:00:00.000Z"), which also split into 3 parts on '-' but
// produces garbage ("08/24T06:00:00.000Z/2026", confirmed 2026-08-26). Strip
// to the date-only portion first.
function formatInvoiceDate(isoDateTime) {
    if (!isoDateTime) return ''
    return DateFormatMAS90(String(isoDateTime).slice(0, 10))
}

const EXPORT_HEADERS = ['Invoice No', 'Invoice Date', 'Customer No', 'Bill To Name', 'Salesperson', 'Item Code', 'Item Description', 'Price Level', 'Unit Price', 'Level Price', 'Qty', 'Total Real', 'Total Expected', 'Difference']
const EXPORT_KEYS = ['InvoiceNo', 'InvoiceDateFmt', 'CustomerNo', 'BillToName', 'SalespersonNo', 'ItemCode', 'ItemCodeDesc', 'priceLevel', 'UnitPrice', 'levelPrice', 'QuantityShipped', 'totalReal', 'totalExpected', 'difference']

/**
 * Price Change Report V3 (2026-08-26) — rebuilds the client's own sample PDF
 * ("Price change report") as a v3/RDS-only report: for invoices that came
 * out of Sales Order V3, compares what the salesperson actually charged
 * (UnitPrice, freely editable in SO V3 above the price floor) against what
 * the customer's CURRENT price level says the item should cost
 * (product_pricing.level{N}Price, resolved via customer_price_level). See
 * priceChangeReport.ts's class header for the full rationale, including why
 * this only covers invoices traced back to a SO V3 order (via
 * AR_InvoiceHistoryHeader.SalesOrderNo = sales_orders_aws_v2.order_no) and
 * why the "Level Price" comparison is always against TODAY's catalog, not a
 * historical reconstruction (product_pricing has no point-in-time history —
 * same limitation the client's own original report had).
 *
 * Grouped by invoice folio, paginated by GROUP (2026-08-26, clarified — the
 * client wants pagination, just never splitting one invoice's lines across
 * two pages, same as ProductPricing.js/CustomerPriceLevels.js). The Excel
 * export is a SEPARATE unpaginated fetch (getReportExport) covering the
 * whole filtered range, not just whatever page is on screen.
 */
export default class PriceChangeReportV3 extends Component {
    constructor(props) {
        super(props)
        this.ModalLoading = React.createRef()
    }

    state = {
        companies: [],
        idCompany: getValueCookie('CompanyId') || '',
        dateStart: formatDateInput(addDays(new Date(), -30)),
        dateEnd: formatDateInput(new Date()),
        invoices: [],
        lines: [],
        summary: { invoiceCount: 0, totalQty: 0, totalReal: 0, totalExpected: 0 },
        page: 1,
        pageSize: 10,
        total: 0,
        loading: false,
        loaded: false,
        errorMessage: '',
        exportRows: [],
        // Excel button visual bugs (2026-08-27, client's ask — both flagged
        // as confusing to the real end client, "el cliente lo toma error").
        // See loadReport()/prepareExport()/the button JSX for the full
        // writeup. This tracks whether the export fetch is ACTUALLY in
        // flight, so the button only ever says "Preparing..." while
        // genuinely preparing — never merely because nobody's hovered yet.
        isPreparingExport: false,
    }

    componentDidMount() {
        this.fetchCompanies()
    }

    async fetchCompanies() {
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/companies', {})
            if (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) {
                this.setState({ companies: resp.data })
            }
        } catch (error) {
            console.error('fetchCompanies failed:', error)
        }
    }

    onChangeField = (field, value) => {
        this.setState({ [field]: value })
    }

    async loadReport(page) {
        const { idCompany, dateStart, dateEnd, pageSize } = this.state
        if (!idCompany || !dateStart || !dateEnd) return
        const targetPage = page || 1
        this.setState({ loading: true, errorMessage: '', page: targetPage })
        this.ModalLoading.current.showState(true)
        try {
            const resp = await getInformationWithDataV3('/v3/reports/price-change/list', {
                idCompany: Number(idCompany), dateStart, dateEnd, page: targetPage, pageSize,
            })
            if (!resp || !resp.status || resp.status.code !== 1) {
                this.setState({ loading: false, loaded: true, errorMessage: 'Could not load the report.' })
                return
            }
            const invoices = (resp.data && Array.isArray(resp.data.invoices)) ? resp.data.invoices : []
            const lines = (resp.data && Array.isArray(resp.data.lines)) ? resp.data.lines : []
            const summary = (resp.data && resp.data.summary) ? resp.data.summary : { invoiceCount: 0, totalQty: 0, totalReal: 0, totalExpected: 0 }
            this.setState({ loading: false, loaded: true, invoices, lines, summary, total: resp.total || invoices.length })
            // Every page reload invalidates the previously-prepared export —
            // it must always reflect the CURRENT filters, not stale ones.
            this.setState({ exportRows: [] })
            // Kick off the export fetch right away in the background (2026-08-27
            // fix — replaces the old onMouseEnter-triggered fetch, which made the
            // button visibly flash through "Preparing..." the instant the admin
            // moved the mouse over it, which the client read as an error, not a
            // fetch). Not awaited — the report itself is already on screen,
            // there's nothing to block. The button just quietly goes from
            // disabled to enabled once this resolves, no hover needed at all.
            if (summary.invoiceCount > 0) this.prepareExport()
        } catch (error) {
            console.error('loadReport failed:', error)
            this.setState({ loading: false, loaded: true, errorMessage: 'Could not load the report — the server did not respond.' })
        } finally {
            this.ModalLoading.current.showState(false)
        }
    }

    /**
     * Pulls the FULL filtered range (unpaginated), always matching the
     * current filters, not just the page on screen. Called automatically
     * right after loadReport() succeeds (2026-08-27 — no longer hover-
     * triggered, see loadReport's own comment). Runs silently in the
     * background — does NOT use the shared ModalLoading overlay, since the
     * report itself is already visible and usable; isPreparingExport alone
     * drives the button's own "Preparing..." label.
     */
    async prepareExport() {
        const { idCompany, dateStart, dateEnd, exportRows } = this.state
        if (!idCompany || !dateStart || !dateEnd || exportRows.length > 0) return
        this.setState({ isPreparingExport: true })
        try {
            const resp = await getInformationWithDataV3('/v3/reports/price-change/export', {
                idCompany: Number(idCompany), dateStart, dateEnd,
            })
            const rows = (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) ? resp.data : []
            const built = rows.map((r) => {
                const unitPrice = Number(r.UnitPrice) || 0
                const qty = Number(r.QuantityShipped) || 0
                const levelPrice = (r.levelPrice !== null && r.levelPrice !== undefined) ? Number(r.levelPrice) : null
                const totalReal = unitPrice * qty
                const totalExpected = levelPrice !== null ? levelPrice * qty : null
                return {
                    ...r,
                    InvoiceDateFmt: formatInvoiceDate(r.InvoiceDate),
                    UnitPrice: unitPrice.toFixed(2),
                    levelPrice: levelPrice !== null ? levelPrice.toFixed(2) : 'N/A',
                    totalReal: totalReal.toFixed(2),
                    totalExpected: totalExpected !== null ? totalExpected.toFixed(2) : 'N/A',
                    difference: totalExpected !== null ? (totalReal - totalExpected).toFixed(2) : 'N/A',
                }
            })
            this.setState({ exportRows: built })
        } catch (error) {
            console.error('prepareExport failed:', error)
        } finally {
            this.setState({ isPreparingExport: false })
        }
    }

    render() {
        const { companies, idCompany, dateStart, dateEnd, invoices, lines, summary, page, pageSize, total, loading, loaded, errorMessage, exportRows, isPreparingExport } = this.state
        const totalPages = Math.max(1, Math.ceil(total / pageSize))
        const totalDifference = summary.totalReal - summary.totalExpected
        const hasData = loaded && summary.invoiceCount > 0

        const linesByInvoice = {}
        for (const l of lines) {
            const key = l.InvoiceNo
            if (!linesByInvoice[key]) linesByInvoice[key] = []
            linesByInvoice[key].push(l)
        }

        return (
            <div className="dash-page pb-5">
                <Menu Refresh={this.state.Refresh} Father={this} />
                <LoadingWindow ref={this.ModalLoading} />

                <div style={{ marginTop: 30, background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #2563EB 100%)', padding: '2rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '12px', padding: '0.75rem', display: 'flex' }}>
                            <AiOutlineTag style={{ color: 'white', fontSize: '1.75rem' }} />
                        </div>
                        <div>
                            <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.375rem', margin: 0 }}>Price Change Report (V3)</h1>
                            <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.8rem' }}>What was charged vs. the customer's price level — Sales Order V3 invoices only</p>
                        </div>
                    </div>
                </div>

                <div className="container-fluid" style={{ marginTop: 24 }}>
                    {errorMessage && (
                        <div className="metric-card status-danger mb-3">
                            <div style={{ color: '#DC2626', fontSize: '0.85rem' }}>{errorMessage}</div>
                        </div>
                    )}

                    <div className="row g-3 mb-4 align-items-end justify-content-center">
                        <div className="col-auto">
                            <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', marginBottom: '4px' }}>Company</label>
                            <select value={idCompany} onChange={(e) => this.onChangeField('idCompany', e.target.value)}
                                style={{ padding: '0.6rem 0.85rem', border: '1.5px solid #E2E8F0', borderRadius: '10px', fontSize: '0.9rem', minWidth: '200px' }}>
                                <option value="">Select a company...</option>
                                {companies.map((c) => (
                                    <option key={c.idCompany} value={c.idCompany}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-auto">
                            <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', marginBottom: '4px' }}>From</label>
                            <input type="date" value={dateStart} onChange={(e) => this.onChangeField('dateStart', e.target.value)}
                                style={{ padding: '0.6rem 0.85rem', border: '1.5px solid #E2E8F0', borderRadius: '10px', fontSize: '0.9rem' }} />
                        </div>
                        <div className="col-auto">
                            <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', marginBottom: '4px' }}>To</label>
                            <input type="date" value={dateEnd} onChange={(e) => this.onChangeField('dateEnd', e.target.value)}
                                style={{ padding: '0.6rem 0.85rem', border: '1.5px solid #E2E8F0', borderRadius: '10px', fontSize: '0.9rem' }} />
                        </div>
                        <div className="col-auto">
                            <button onClick={() => this.loadReport(1)} type="button" disabled={!idCompany}
                                style={{
                                    background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
                                    border: 'none', borderRadius: '10px', color: 'white', padding: '0.7rem 1.5rem',
                                    fontWeight: 600, fontSize: '0.9rem', cursor: idCompany ? 'pointer' : 'default', opacity: idCompany ? 1 : 0.5,
                                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                                }}>
                                <AiOutlineReload /> Load Data
                            </button>
                        </div>
                        {/* Excel button (2026-08-26, client's ask). Two real bugs fixed
                            2026-08-27:
                            1) The export used to fetch lazily on hover, and the "Preparing..."
                               label was driven by exportRows.length === 0 — ALSO true before
                               the very first hover, so it said "Preparing..." the instant the
                               report loaded, then visibly changed again on hover, which the
                               client read as an error ("apenas paso el mouse y pasan muchas
                               cosas"). loadReport() now kicks off prepareExport() itself right
                               after loading, in the background — nothing happens on hover
                               anymore, the button just quietly becomes usable once ready.
                            2) <ExcelDocument> wraps react-data-export's <ExcelFile>, whose
                               click-to-download isn't actually gated by the `disabled` prop on
                               its inner button — clicking it before any data loaded (exportRows
                               still []) silently downloaded a file with empty/garbage content.
                               Fixed by not mounting the real downloadable component AT ALL until
                               exportRows has rows — nothing exists to click on prematurely. */}
                        <div className="col-auto">
                            {hasData && exportRows.length > 0 ? (
                                <ExcelDocument
                                    hidden={getValueCookie('TypeUser') !== '1'}
                                    buttonTitle={'Export Excel'}
                                    data={getDataSet(exportRows, EXPORT_HEADERS, EXPORT_KEYS)}
                                    sheetname={'Price Change Report'}
                                    archname={'Price Change Report ' + getValueCookie('Company') + ' ' + dateStart + '_' + dateEnd}
                                />
                            ) : (
                                <button type="button" disabled
                                    hidden={getValueCookie('TypeUser') !== '1'}
                                    style={{ padding: '0.75rem 1rem', height: '45px', fontSize: '0.8375rem', display: 'flex', alignItems: 'center', gap: '0.35rem', opacity: 0.6, cursor: 'default' }}
                                    className="btn greenButton">
                                    <AiFillFileExcel /> {isPreparingExport ? 'Preparing...' : 'Export Excel'}
                                </button>
                            )}
                        </div>
                    </div>

                    {!loaded && !loading && (
                        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#94A3B8' }}>
                            <AiOutlineReload style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.4 }} />
                            <p style={{ margin: 0, fontSize: '1rem' }}>Select a company and date range, then click <strong style={{ color: '#1e3a8a' }}>Load Data</strong>.</p>
                        </div>
                    )}

                    {loaded && (
                        <>
                            <div className="row g-3 mb-4">
                                <div className="col-md-3">
                                    <div className="metric-card status-primary">
                                        <div className="metric-label">Invoices (full range)</div>
                                        <div className="metric-value">{summary.invoiceCount}</div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="metric-card status-success">
                                        <div className="metric-label">Total Real</div>
                                        <div className="metric-value">${NumberFormat(pricesFormat(summary.totalReal))}</div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="metric-card status-warning">
                                        <div className="metric-label">Total By Level</div>
                                        <div className="metric-value">${NumberFormat(pricesFormat(summary.totalExpected))}</div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className={'metric-card ' + (totalDifference < 0 ? 'status-danger' : 'status-primary')}>
                                        <div className="metric-label">Total Difference</div>
                                        <div className="metric-value">${NumberFormat(pricesFormat(totalDifference))}</div>
                                    </div>
                                </div>
                            </div>

                            {invoices.length === 0 && !loading && (
                                <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#94A3B8' }}>
                                    <AiOutlineWarning style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.4 }} />
                                    <p style={{ margin: 0, fontSize: '1rem' }}>No invoices found in this date range for this company.</p>
                                </div>
                            )}

                            {invoices.length > 0 && (
                                <div className="card">
                                    <div className="tableFixHeadSmall">
                                        <table className="table table-clean">
                                            <thead>
                                                <tr>
                                                    <th>Item Code</th>
                                                    <th>Description</th>
                                                    <th className="text-center">Level</th>
                                                    <th className="text-end">Unit Price</th>
                                                    <th className="text-end">Level Price</th>
                                                    <th className="text-end">Qty</th>
                                                    <th className="text-end">Total Real</th>
                                                    <th className="text-end">Total Expected</th>
                                                    <th className="text-end">Difference</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {invoices.map((inv) => {
                                                    const invLines = linesByInvoice[inv.InvoiceNo] || []
                                                    let invReal = 0, invExpected = 0, invHasExpected = false
                                                    for (const l of invLines) {
                                                        const qty = Number(l.QuantityShipped) || 0
                                                        invReal += (Number(l.UnitPrice) || 0) * qty
                                                        if (l.levelPrice !== null && l.levelPrice !== undefined) {
                                                            invExpected += Number(l.levelPrice) * qty
                                                            invHasExpected = true
                                                        }
                                                    }
                                                    const invDiff = invReal - invExpected
                                                    return (
                                                        <React.Fragment key={inv.InvoiceNo}>
                                                            {/* Group separator (2026-08-26, client's ask — "un color claro
                                                                para que se distinga donde termina y comienza otra invoice").
                                                                Solid brand-blue tint + a thick top border, clearly
                                                                different from the plain white body rows below it — not a
                                                                near-white tint, which the client correctly said was
                                                                invisible. */}
                                                            <tr style={{ background: '#DBEAFE', borderTop: '3px solid #2563EB' }}>
                                                                <td colSpan={9} style={{ padding: '10px 16px', borderLeft: '4px solid #2563EB' }}>
                                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', fontWeight: 800, fontSize: '0.8rem', color: '#1e3a8a' }}>
                                                                        <span>#{inv.InvoiceNo}</span>
                                                                        <span style={{ fontWeight: 600, color: '#334155' }}>{formatInvoiceDate(inv.invoiceDate)}</span>
                                                                        <span style={{ fontWeight: 600, color: '#334155' }}>{inv.customerNo} — {inv.billToName}</span>
                                                                        <span style={{ fontWeight: 600, color: '#334155' }}>Salesperson: {inv.salespersonNo}</span>
                                                                        {invHasExpected ? (
                                                                            <span style={{ marginLeft: 'auto', fontWeight: 700, color: invDiff < 0 ? '#DC2626' : '#1e3a8a' }}>
                                                                                Diff: ${NumberFormat(pricesFormat(invDiff))}
                                                                            </span>
                                                                        ) : invLines.length === 0 && (
                                                                            // Every line on this invoice had zero real
                                                                            // difference and got filtered out (2026-08-27,
                                                                            // client's ask) — without this, the invoice
                                                                            // showed just a header with no rows under it,
                                                                            // which looked broken rather than "nothing to
                                                                            // see here."
                                                                            <span style={{ marginLeft: 'auto', fontWeight: 700, fontStyle: 'italic', color: '#64748B' }}>
                                                                                No differences
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            {invLines.map((l, i) => {
                                                                const qty = Number(l.QuantityShipped) || 0
                                                                const unitPrice = Number(l.UnitPrice) || 0
                                                                const levelPrice = (l.levelPrice !== null && l.levelPrice !== undefined) ? Number(l.levelPrice) : null
                                                                const totalReal = unitPrice * qty
                                                                const totalExpected = levelPrice !== null ? levelPrice * qty : null
                                                                const diff = totalExpected !== null ? totalReal - totalExpected : null
                                                                return (
                                                                    <tr key={inv.InvoiceNo + '_' + i}>
                                                                        <td className="mono" style={CELL_PADDING}>{l.ItemCode}</td>
                                                                        <td className="small" style={CELL_PADDING}>{l.ItemCodeDesc || '—'}</td>
                                                                        <td className="text-center" style={CELL_PADDING}>{l.priceLevel}</td>
                                                                        <td className="text-end" style={CELL_PADDING}>${NumberFormat(pricesFormat(unitPrice))}</td>
                                                                        <td className="text-end" style={CELL_PADDING}>{levelPrice !== null ? '$' + NumberFormat(pricesFormat(levelPrice)) : 'N/A'}</td>
                                                                        <td className="text-end" style={CELL_PADDING}>{qty}</td>
                                                                        <td className="text-end" style={CELL_PADDING}>${NumberFormat(pricesFormat(totalReal))}</td>
                                                                        <td className="text-end" style={CELL_PADDING}>{totalExpected !== null ? '$' + NumberFormat(pricesFormat(totalExpected)) : 'N/A'}</td>
                                                                        <td className="text-end" style={{ ...CELL_PADDING, color: diff !== null && diff < 0 ? '#DC2626' : undefined }}>
                                                                            {diff !== null ? '$' + NumberFormat(pricesFormat(diff)) : 'N/A'}
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            })}
                                                        </React.Fragment>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', padding: '12px 16px' }}>
                                        <span style={{ fontSize: '0.8rem', color: '#64748B' }}>Page {page} of {totalPages} ({total} invoices)</span>
                                        <button style={{ ...BTN_SECONDARY, opacity: page <= 1 ? 0.5 : 1 }}
                                            disabled={page <= 1} onClick={() => this.loadReport(page - 1)}>Previous</button>
                                        <button style={{ ...BTN_SECONDARY, opacity: page >= totalPages ? 0.5 : 1 }}
                                            disabled={page >= totalPages} onClick={() => this.loadReport(page + 1)}>Next</button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        )
    }
}
